import { JSX } from 'react';
import { LayoutContainer, MiniLink, PageSection, PageSectionContent } from '@greystar/designsystem';
import { Property, goBackToComponent, useAnalytics } from '@greystar/lib';
import { PropertyDetailsCtasRowProps } from '@greystar/ui';
import {
  GetStaticComponentProps,
  RichText as JssRichText,
  useComponentProps,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { useRouter } from 'next/router'; // Import useRouter

/**
 * PropertyDetailsCtasRow Component
 * Renders a text description followed by a row of four CTA buttons wrapped in anchor tags.
 * @param props - The properties for the PropertyDetailsCtasRow component.
 * @param props.fields - The fields provided by the datasource (contains text).
 * @param props.rendering.uid - The unique ID for fetching component props.
 * @param props.params - Parameters like DynamicPlaceholderId.
 * @returns A JSX element rendering the PropertyDetailsCtasRow.
 */
export const PropertyDetailsCtasRow = (props: PropertyDetailsCtasRowProps): JSX.Element => {
  const { fields, rendering } = props;

  const data = useComponentProps<{ property: Property }>(rendering.uid);
  const hasTours = data?.property?.tours && data.property.tours.length > 0;
  const analytics = useAnalytics();
  const router = useRouter(); // Initialize router
  const isCalculatorPage = router.asPath.includes('/calculator'); // Check if on calculator page

  const handleButtonClick = (
    eventName: string,
    clickText: string,
    clickType: 'internal' | 'external',
    destinationUrl: string
  ) => {
    if (!data?.property) return;

    const property = data.property;
    const propertyLocation = `${property?.location?.address}, ${property?.location?.city}, ${property?.location?.state}, ${property?.location?.postalCode}`;

    const eventData = {
      event: eventName,
      action_type: 'entry',
      state: property.location?.state ?? '',
      property_location: propertyLocation ?? '',
      property_id: property.id ?? '',
      property_name: property.name ?? '',
      module_name: 'property details page',
      inpage_location: 'middle',
      click_text: clickText,
      click_type: clickType ?? 'internal',
      destination_url: destinationUrl ?? property?.contact?.leaseOnlineUrl,
    };
    analytics.trackEvent(eventName, eventData);
  };

  return (
    <PageSection isBottomFlushed="true" isTopFlushed="true">
      <PageSectionContent>
        {/* Legal Text from Sitecore */}
        <div className="mb-4">
          <JssRichText className="text-blue-600 text-lg" field={fields?.text} />
        </div>
        {/* CTA Buttons Row */}
        <LayoutContainer flexWrap="wrap" gap="xs">
          <MiniLink
            variant="primary"
            label="Apply"
            href={data?.property?.contact?.leaseOnlineUrl}
            icon={{ position: 'right', name: 'ExternalLink' }}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() =>
              handleButtonClick(
                'property_apply_entry',
                'Apply',
                'external',
                data?.property?.contact?.leaseOnlineUrl || ''
              )
            }
          />

          {hasTours && (
            <MiniLink
              label="Book a Tour"
              variant="tertiary"
              href={data?.property?.contact?.communityWebsiteTrackingUrl || '#book-a-tour'}
              icon={
                data?.property?.contact?.communityWebsiteTrackingUrl
                  ? { position: 'right', name: 'ExternalLink' }
                  : undefined
              }
              onClick={() => {
                handleButtonClick(
                  'property_book_virtual_tour_entry',
                  'Book a Tour',
                  'internal',
                  isCalculatorPage
                    ? ''
                    : data?.property?.contact?.communityWebsiteTrackingUrl || '#book-a-tour'
                );
                if (isCalculatorPage) {
                  goBackToComponent(router, 'book-a-tour');
                }
              }}
              {...(data?.property?.contact?.communityWebsiteTrackingUrl
                ? { target: '_blank', rel: 'noopener noreferrer' }
                : {})}
            />
          )}

          <MiniLink
            label="Contact Us"
            variant="tertiary"
            href={'#contact-us'} // Use undefined href for router navigation
            onClick={() => {
              handleButtonClick(
                'property_contact_us_entry',
                'Contact Us',
                'internal',
                isCalculatorPage ? '' : '#contact-us'
              );
              if (isCalculatorPage) {
                goBackToComponent(router, 'contact-us'); // Redirect to CDP page with anchor
              }
            }}
          />
          <MiniLink
            label="Resident Portal"
            variant="tertiary"
            href={data?.property?.contact?.residentPortalUrl}
            onClick={() =>
              handleButtonClick(
                'property_resident_portal_entry',
                'Resident Portal',
                'external',
                data?.property?.contact?.residentPortalUrl || ''
              )
            }
            icon={
              data?.property?.contact?.residentPortalUrl
                ? { position: 'right', name: 'ExternalLink' }
                : undefined
            }
            {...(data?.property?.contact?.residentPortalUrl
              ? { target: '_blank', rel: 'noopener noreferrer' }
              : {})}
          />
        </LayoutContainer>
      </PageSectionContent>
    </PageSection>
  );
};

/**
 * getStaticProps to fetch property data for the component.
 * @param rendering - The rendering context.
 * @param layoutData - The layout data containing property information.
 * @returns The property data to be used by the component.
 */
export const getStaticProps: GetStaticComponentProps = async (
  _rendering,
  layoutData
): Promise<{ property: Property }> => {
  const property = layoutData.sitecore.context.property as Property;
  return {
    property: {
      id: property.id,
      name: property.name,
      location: property.location,
      contact: property.contact,
      tours: property.tours ?? [],
      availableUnits: [],
    },
  };
};

export default withDatasourceCheck()(PropertyDetailsCtasRow);
